import React, { useState, useEffect } from 'react';
import Masonry from 'react-masonry-css';
import classNames from 'classnames';

import {
  IAssetWith5050TextFields,
  IMilestoneFields,
  IQuickStartGuideFields
} from '../../../types/contentful';

import { breakpoints } from '../../styles/media-queries';

import { QuickStartGuideBox } from '../QuickStartGuideBox';
import { ScrollReveal } from '../ScrollReveal';

import { Milestone } from '../Milestone';
import { AssetWith5050Text } from '../AssetWith5050Text';

function renderBeforeAfterComponent(
  component: IQuickStartGuideFields['afterImageWith5050text']
) {
  switch (component?.sys?.contentType?.sys?.id) {
    case 'assetWith5050Text':
      return (
        <AssetWith5050Text
          {...(component.fields as IAssetWith5050TextFields)}
        />
      );
    case 'milestone':
      return <Milestone {...(component.fields as IMilestoneFields)} />;
    default:
      return null;
  }
}

export function QuickStartGuide({
  beforeImageWith5050text,
  afterImageWith5050text,
  beforeBoxes,
  afterBoxes
}: IQuickStartGuideFields) {
  const [isMobile, setIsMobile] = useState(false);
  const [isAfter, setIsAfter] = useState(false);

  useEffect(() => {
    setIsMobile(
      typeof window !== `undefined` && window.innerWidth < breakpoints.tablet
    );
    setIsAfter(window.location.href.includes('after'));
  }, []);

  return (
    <>
      <div
        className="block-container my-12 tablet:my-16"
        data-blockid="quickStartGuideToggle"
      >
        <div className="w-full px-3 pt-0 tablet:px-12 desktop:w-10/12 desktop:pt-1">
          <div className="flex w-full rounded-[.75rem] bg-[#F3F3F3]">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsAfter(false)}
              onKeyDown={(e) => e.key === 'Enter' && setIsAfter(false)}
              className={classNames(
                'quick-start-guide-toggle-item',
                !isAfter &&
                  'bg-white font-medium shadow-[0px_0px_16px_rgba(0,0,0,0.16)]'
              )}
            >
              <p className="w-[60%] min-w-[130px] tablet:max-w-none">
                Set up your account
              </p>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsAfter(true)}
              onKeyDown={(e) => e.key === 'Enter' && setIsAfter(true)}
              className={classNames(
                'quick-start-guide-toggle-item',
                isAfter &&
                  'bg-white font-medium shadow-[0px_0px_16px_rgba(0,0,0,0.16)]'
              )}
            >
              <p className="w-[60%] min-w-[130px] tablet:max-w-none">
                Set up your kids
              </p>
            </div>
          </div>
        </div>
      </div>
      {!isAfter ? (
        <>
          {renderBeforeAfterComponent(beforeImageWith5050text)}
          <ScrollReveal>
            <div
              className="block-container mb-12 mt-0 tablet:mb-16 tablet:mt-16"
              data-blockid="beforeBoxes"
            >
              <div className="w-full px-3 tablet:px-12">
                <div className="block-layout-gutter">
                  <div className="box-column w-full p-2 pb-2 pt-0 tablet:p-3 desktop:w-10/12 desktop:px-4 desktop:pb-4 desktop:pt-1">
                    {isMobile &&
                      (beforeBoxes || []).map(({ sys, fields }) => (
                        <QuickStartGuideBox {...fields} key={sys?.id} />
                      ))}
                    {!isMobile && (
                      <div className="masonry-wrapper">
                        <Masonry
                          breakpointCols={2}
                          className="masonry-grid"
                          columnClassName="masonry-grid-column"
                        >
                          {(beforeBoxes || []).map(({ sys, fields }) => (
                            <QuickStartGuideBox {...fields} key={sys?.id} />
                          ))}
                        </Masonry>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </>
      ) : (
        <>
          {renderBeforeAfterComponent(afterImageWith5050text)}
          <ScrollReveal>
            <div
              className="block-container mb-12 mt-0 tablet:mb-16 tablet:mt-16"
              data-blockid="afterBoxes"
            >
              <div className="w-full px-3 tablet:px-12">
                <div className="block-layout-gutter">
                  <div className="box-column w-full p-2 pb-2 pt-0 tablet:p-3 desktop:w-10/12 desktop:px-4 desktop:pb-4 desktop:pt-1">
                    {isMobile &&
                      (afterBoxes || []).map(({ sys, fields }) => (
                        <QuickStartGuideBox {...fields} key={sys?.id} />
                      ))}
                    {!isMobile && (
                      <div className="masonry-wrapper">
                        <Masonry
                          breakpointCols={2}
                          className="masonry-grid"
                          columnClassName="masonry-grid-column"
                        >
                          {(afterBoxes || []).map(({ sys, fields }) => (
                            <QuickStartGuideBox {...fields} key={sys?.id} />
                          ))}
                        </Masonry>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </>
      )}
    </>
  );
}
